<template>
    <div class="w-full rounded bg-white" :class="{ 'shadow-lg pb-6': isAdvanced }">
        <div :class="{ 'w-full bg-gray-200': !isAdvanced }">
            <div
                :class="{
                    'flex pb-6 flex-col px-0 rounded bg-white shadow-lg': !isAdvanced,
                    'pt-4': !massMailing,
                }"
            >
                <div class="px-6 grid grid-cols-1 gap-6">
                    <div data-testid="email-composer-subject-field">
                        <label>{{ $t('responseLeadXpress.subject') }}</label>
                        <div class="relative">
                            <activix-input
                                data-testid="email-composer-subject-input"
                                :value="subject"
                                ref="subject"
                                @input="updateSubject"
                            />
                            <skeleton-loading
                                class="absolute inset-0 h-10 rounded"
                                data-testid="email-ai-subject-loading"
                                v-if="showAiDraftSubjectLoading"
                            />
                        </div>
                    </div>
                    <div
                        class="relative flex-1 flex flex-col"
                        data-testid="email-composer-body-field"
                        :class="{ 'gap-0': showAiDraftControls, 'space-y-6': !showAiDraftControls }"
                    >
                        <template-editor
                            :locale="locale"
                            :lead="lead"
                            :mass-mailing="massMailing"
                            :design="design"
                            :sender="sender"
                            :key="template?.id ?? 0"
                            @update-design="updateDesign"
                            @update-body="updateBody"
                            v-if="isAdvanced"
                        />
                        <template v-else>
                            <div class="relative" data-testid="email-composer-editor">
                                <tiptap
                                    :model-value="body"
                                    :min-height="550"
                                    :max-height="700"
                                    :disabled="
                                        communicating || aiDraftState === EmailAiDraftState.Loading
                                    "
                                    :history="history"
                                    :add-last-email-body="addLastEmailBody"
                                    :original-communication="originalCommunication"
                                    :size="composerSize"
                                    :lead="lead"
                                    :video="video"
                                    :locale="locale"
                                    :show-brochure="true"
                                    :show-attributes="true"
                                    :hide-resize-bar="showAiDraftControls"
                                    :sender="sender"
                                    :key="aiDraftEditorRenderKey"
                                    ref="editor"
                                    @model-value-applied="handleComposerModelValueApplied"
                                    @update:model-value="updateBody"
                                    @video-cancelled="$emit('videoCancelled', $event)"
                                >
                                    <template #after-toolbar>
                                        <email-ai-draft-controls
                                            :can-generate="canGenerateAiDraft"
                                            :can-undo="aiDraftCanUndo"
                                            :can-update="canUpdateAiDraft"
                                            :has-generated-draft="hasLocaleGeneratedDraft"
                                            :rate-limit-seconds="aiDraftRateLimitSeconds"
                                            :service-lead-disabled="aiDraftServiceLeadDisabled"
                                            :state="aiDraftState"
                                            :update-disabled-reason="aiDraftUpdateDisabledReason"
                                            @generate="$emit('generate-ai-draft', locale)"
                                            @undo="$emit('undo-ai-draft', locale)"
                                            @update="
                                                $emit('update-ai-draft', {
                                                    locale,
                                                    instruction: $event.instruction,
                                                })
                                            "
                                            v-if="showAiDraftControls"
                                        />
                                    </template>
                                    <template #after-editor-content>
                                        <ai-draft-feedback-controls
                                            :feedback="aiDraftFeedback"
                                            i18n-namespace="mailClient.aiDraft"
                                            testid-prefix="email-ai"
                                            @select-feedback="
                                                $emit('select-ai-draft-feedback', $event)
                                            "
                                            @feedback-submit="
                                                $emit('submit-ai-draft-feedback', $event)
                                            "
                                            v-if="showAiDraftFeedbackControls"
                                        />
                                    </template>
                                </tiptap>
                                <email-ai-draft-loading-overlay
                                    @stop="$emit('stop-ai-draft')"
                                    v-if="showAiDraftLoadingOverlay"
                                />
                            </div>
                            <div
                                class="px-0 pt-3 text-center text-xs text-gray-500"
                                data-testid="email-ai-disclaimer"
                                v-if="showAiDraftControls"
                            >
                                {{ $t('mailClient.aiDraft.disclaimer') }}
                            </div>
                        </template>
                    </div>
                    <div class="w-full text-right">
                        <label class="hidden | xs:block xs:invisible" v-if="showFrom">{{
                            $t('responseLeadXpress.addAttachment')
                        }}</label>
                        <activix-drag-upload
                            class="truncate"
                            :model-value="attachments"
                            :maximum-size="attachmentParameters.maximumAttachmentSize"
                            :multiple="true"
                            :disabled="disallowCommunication"
                            @update:model-value="updateAttachment"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'pinia';
    import { computed, toRef } from 'vue';
    import TemplateEditor from '@/components/templates/TemplateEditor.vue';
    import ActivixDragUpload from '@/components/elements/ActivixDragUpload.vue';
    import { useGlobalStore } from '@/store/store.ts';
    import Tiptap from '@/components/composer/Tiptap.vue';
    import EmailAiDraftControls from '@/components/lead_xpress/aiDraft/EmailAiDraftControls.vue';
    import AiDraftFeedbackControls from '@/components/aiDraft/feedback/AiDraftFeedbackControls.vue';
    import EmailAiDraftLoadingOverlay from '@/components/lead_xpress/aiDraft/EmailAiDraftLoadingOverlay.vue';
    import SkeletonLoading from '@/components/loading/SkeletonLoading.vue';
    import { useEmailAiDraftFeedbackVisibility } from '@/components/lead_xpress/aiDraft/useEmailAiDraftFeedbackVisibility.ts';
    import { EmailAiDraftState } from '@/components/lead_xpress/aiDraft/useEmailAiDraft.ts';
    import Lead from '../../entities/Lead.js';

    export default {
        name: 'LeadXpressTranslate',

        emits: [
            'translation-updated',
            'update-subject',
            'update-design',
            'update-body',
            'update-attachment',
            'generate-ai-draft',
            'update-ai-draft',
            'undo-ai-draft',
            'stop-ai-draft',
            'select-ai-draft-feedback',
            'submit-ai-draft-feedback',
            'videoCancelled',
        ],

        components: {
            Tiptap,
            EmailAiDraftControls,
            AiDraftFeedbackControls,
            EmailAiDraftLoadingOverlay,
            SkeletonLoading,
            ActivixDragUpload,
            TemplateEditor,
        },

        props: {
            subject: {
                type: String,
                required: false,
            },
            design: {
                type: Object,
                default: null,
            },
            template: {
                type: Object,
                default: null,
            },
            sender: {
                type: Object,
                default: null,
            },
            body: {
                type: String,
                required: false,
            },
            locale: {
                type: String,
                required: true,
            },
            attachments: {
                type: Array,
                required: true,
            },
            isAdvanced: {
                type: Boolean,
                default: false,
            },
            lead: {
                type: Lead,
                required: true,
            },
            massMailing: {
                type: Boolean,
                default: false,
            },
            communicating: {
                type: Boolean,
                default: false,
            },
            draft: {
                type: Object,
                default: () => {},
            },
            video: {
                type: Object,
            },
            history: {
                type: String,
                default: '',
            },
            addLastEmailBody: {
                type: Boolean,
                default: false,
            },
            originalCommunication: {
                type: String,
                default: '',
            },
            aiDraftState: {
                type: String,
                default: 'idle',
            },
            aiDraftFeedback: {
                type: String,
                default: null,
            },
            aiDraftEnabled: {
                type: Boolean,
                default: false,
            },
            aiDraftHasGeneratedDraft: {
                type: Boolean,
                default: false,
            },
            aiDraftGeneratedDraft: {
                type: Object,
                default: null,
            },
            aiDraftRateLimitSeconds: {
                type: Number,
                default: 60,
            },
            canGenerateAiDraft: {
                type: Boolean,
                default: false,
            },
            canUpdateAiDraft: {
                type: Boolean,
                default: false,
            },
            aiDraftUpdateDisabledReason: {
                type: String,
                default: null,
            },
            aiDraftCanUndo: {
                type: Boolean,
                default: false,
            },
            aiDraftServiceLeadDisabled: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                aiDraftEditorRenderKey: 0,
                pendingAiDraftInsertedBody: null,
            };
        },

        setup(props) {
            const hasLocaleGeneratedDraft = computed(
                () =>
                    props.aiDraftHasGeneratedDraft &&
                    props.aiDraftGeneratedDraft?.locale === props.locale
            );
            const aiDraftFeedbackVisibility = useEmailAiDraftFeedbackVisibility({
                body: toRef(props, 'body'),
                hasGeneratedDraft: hasLocaleGeneratedDraft,
                state: toRef(props, 'aiDraftState'),
            });

            return {
                aiDraftFeedbackVisibility,
                EmailAiDraftState,
                hasLocaleGeneratedDraft,
                showAiDraftFeedbackControls: aiDraftFeedbackVisibility.showFeedbackControls,
            };
        },

        computed: {
            ...mapState(useGlobalStore, {
                attachmentParameters: 'attachmentParameters',
            }),

            disallowCommunication() {
                return this.communicating && !this.ownCommunication;
            },

            showFrom() {
                return this.massMailing;
            },

            showAiDraftSubjectLoading() {
                return (
                    this.aiDraftEnabled &&
                    this.aiDraftState === EmailAiDraftState.Loading &&
                    !this.subjectStartsWithReply
                );
            },

            showAiDraftLoadingOverlay() {
                return this.aiDraftEnabled && this.aiDraftState === EmailAiDraftState.Loading;
            },

            showAiDraftControls() {
                return (
                    this.aiDraftEnabled &&
                    (this.canGenerateAiDraft ||
                        this.canUpdateAiDraft ||
                        !!this.aiDraftUpdateDisabledReason ||
                        this.aiDraftServiceLeadDisabled ||
                        this.hasLocaleGeneratedDraft ||
                        [
                            EmailAiDraftState.Loading,
                            EmailAiDraftState.Failed,
                            EmailAiDraftState.RateLimited,
                        ].includes(this.aiDraftState))
                );
            },

            subjectStartsWithReply() {
                return /^\s*re:/i.test(this.subject || '');
            },

            composerSize() {
                if (this.showAiDraftControls) {
                    return 'sm';
                }

                return this.smLayout ? 'xs' : 'md';
            },
        },

        watch: {
            translation: {
                deep: true,
                handler(newTranslation) {
                    this.$emit('translation-updated', newTranslation);
                },
            },

            aiDraftState(nextState, previousState) {
                if (
                    previousState === EmailAiDraftState.Loading &&
                    nextState === EmailAiDraftState.Inserted
                ) {
                    this.applyInsertedAiDraftBodyToEditor();

                    return;
                }

                if (nextState !== EmailAiDraftState.Inserted) {
                    this.pendingAiDraftInsertedBody = null;
                }
            },
        },

        methods: {
            updateSubject(subject) {
                this.$emit('update-subject', subject);
            },

            updateDesign(design) {
                this.$emit('update-design', design);
            },

            updateBody(body) {
                if (this.aiDraftState === EmailAiDraftState.Loading) {
                    return;
                }

                if (this.shouldIgnorePendingAiDraftBodyUpdate(body)) {
                    return;
                }

                this.aiDraftFeedbackVisibility.handleBodyChanged(body);
                this.$emit('update-body', body);
            },

            shouldIgnorePendingAiDraftBodyUpdate(body) {
                if (
                    this.aiDraftState !== EmailAiDraftState.Inserted ||
                    !this.hasLocaleGeneratedDraft ||
                    !this.pendingAiDraftInsertedBody
                ) {
                    return false;
                }

                return body !== this.pendingAiDraftInsertedBody;
            },

            handleComposerModelValueApplied(body) {
                this.aiDraftFeedbackVisibility.handleComposerModelValueApplied(body);

                if (this.pendingAiDraftInsertedBody === body) {
                    this.pendingAiDraftInsertedBody = null;
                }
            },

            applyInsertedAiDraftBodyToEditor() {
                this.pendingAiDraftInsertedBody = this.body || '';
                this.aiDraftEditorRenderKey += 1;

                this.$nextTick(() => {
                    if (
                        this.aiDraftState !== EmailAiDraftState.Inserted ||
                        !this.hasLocaleGeneratedDraft ||
                        !this.$refs.editor
                    ) {
                        return;
                    }

                    this.handleComposerModelValueApplied(this.body || '');
                });
            },

            updateAttachment(attachment) {
                this.$emit('update-attachment', attachment);
            },
        },
    };
</script>

<style lang="less">
    .notification-wrapper {
        @apply relative whitespace-nowrap border border-grey-200 p-4 flex items-center;

        width: 363px;

        .subject {
            @apply truncate text-sm text-grey-600;
        }
    }
</style>
